import {
  getAllVerifiedLocations,
  getAllVerifiedLocationsCountAll,
  queryKeys,
} from '@vgrubs/components';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { SimpleLocationEntity } from 'components/src/types/location.types';

const PRELOAD_ROOT_MARGIN = '1000px';

const fetchVerifiedLocations = async ({
  pageParam = 1,
  query = '',
  deleted = false,
}: {
  pageParam?: number;
  query?: string;
  deleted?: boolean;
}) => {
  const { data } = await getAllVerifiedLocations({
    limit: 20,
    page: pageParam,
    query: query || null,
    deleted,
  });

  const locations = data?.data ?? [];
  const nextPage = locations.length === 20 ? pageParam + 1 : undefined;

  return {
    locations,
    nextPage,
  };
};

export const useAdminLocationsInfiniteScroll = ({
  initialQuery = '',
  deleted = false,
}) => {
  const [inputValue, setInputValue] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);
  const observerRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useMemo(() => debounce(setQuery, 500), []);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status } =
    useInfiniteQuery({
      queryKey: queryKeys.admin.verifiedLocations.list(query, deleted),
      queryFn: ({ pageParam = 1 }) =>
        fetchVerifiedLocations({ pageParam, query, deleted }),
      getNextPageParam: (lastPage) => lastPage.nextPage,
      initialPageParam: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes - keeps cache for scroll restoration
    });

  const { data: countData } = useQuery({
    queryKey: queryKeys.admin.verifiedLocations.countAll(query),
    queryFn: async () => {
      const { data: countResponse } = await getAllVerifiedLocationsCountAll({
        query: query || null,
      });
      return countResponse?.data;
    },
    enabled: true,
  });

  const { nonDeletedCount, deletedCount } = countData ?? {
    nonDeletedCount: 0,
    deletedCount: 0,
  };

  const totalCount = nonDeletedCount + deletedCount;

  useEffect(() => {
    const current = observerRef.current;
    if (!current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0,
        rootMargin: `0px 0px ${PRELOAD_ROOT_MARGIN} 0px`,
      }
    );

    observer.observe(current);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    debouncedSearch(e.target.value);
  };

  const locations: SimpleLocationEntity[] =
    data?.pages.flatMap((page) => page.locations) ?? [];

  const SCROLL_KEY = 'admin-locations-scroll';

  const saveScrollPosition = () => {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
  };

  useLayoutEffect(() => {
    if (locations.length === 0) return;
    const saved = sessionStorage.getItem(SCROLL_KEY);
    if (saved !== null) {
      const scrollY = parseInt(saved, 10);
      if (!Number.isNaN(scrollY)) {
        sessionStorage.removeItem(SCROLL_KEY);
        window.scrollTo({ top: scrollY });
      }
    }
  }, [locations.length]);

  return {
    data,
    locations,
    nonDeletedCount,
    deletedCount,
    totalCount,
    inputValue,
    handleInputChange,
    observerRef,
    isFetchingNextPage,
    status,
    saveScrollPosition,
  };
};
